import { useEffect, useRef, useState } from 'react';
import { MEDIA_PROFILE_LABELS } from '@shared/protocol';
import type { MeetingPublic } from '@shared/protocol';
import type { Background } from '../lib/useBackground';
import { playTestTone } from '../lib/testTone';
import { BackgroundPicker } from './BackgroundPicker';
import { canChooseSpeaker, type LocalMedia } from '../lib/useLocalMedia';
import { useAudioLevel } from '../lib/useAudioLevel';
import { CamIcon, MicIcon } from './Icons';
import { MicMeter } from './MicMeter';

interface Props {
  meeting: MeetingPublic | null;
  media: LocalMedia;
  background: Background;
  name: string;
  onNameChange: (name: string) => void;
  onJoin: () => void;
  joining: boolean;
}

/** Pre-join: see yourself, pick devices, decide how you arrive. */
export function Lobby({ meeting, media, background, name, onNameChange, onJoin, joining }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [tested, setTested] = useState(false);

  /* Measured on the capture rather than the processed stream: a background
     effect only ever replaces the video track, and reading the mic through it
     would restart the meter every time someone tried a different backdrop. */
  const level = useAudioLevel(media.stream?.getAudioTracks()[0], media.audioEnabled);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    el.srcObject = background.stream;
    el.play().catch(() => {});
  }, [background.stream]);

  const cameras = media.devices.filter((d) => d.kind === 'videoinput');
  const mics = media.devices.filter((d) => d.kind === 'audioinput');
  const speakers = media.devices.filter((d) => d.kind === 'audiooutput');

  return (
    <main className="page">
      <h1>{meeting?.title ?? 'Join meeting'}</h1>
      <p className="lede">Check your camera and microphone, then join.</p>

      {/* The default needs no announcement — anyone joining an ordinary meeting
          expects an ordinary meeting. The other three change what someone
          should do before they join, so they say so. */}
      {meeting && meeting.media !== 'meeting' ? (
        <p className={meeting.media === 'studio' ? 'notice' : 'faint'} style={{ marginTop: '1rem' }}>
          <strong>{MEDIA_PROFILE_LABELS[meeting.media].name}.</strong>{' '}
          {meeting.media === 'studio'
            ? 'This meeting sends stereo sound with echo cancellation switched off, so it carries music properly. Put headphones on before you join — on open speakers it will feed back for everyone.'
            : MEDIA_PROFILE_LABELS[meeting.media].detail}
        </p>
      ) : null}

      <div className="grid grid-2" style={{ marginTop: '2rem' }}>
        <div className="cell">
          <div className="preview">
            <video ref={videoRef} playsInline autoPlay muted />
          </div>
          <div className="row" style={{ marginTop: '0.75rem' }}>
            <button
              className="control"
              aria-pressed={media.audioEnabled}
              onClick={() => media.setAudioEnabled(!media.audioEnabled)}
              title={media.audioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            >
              <MicIcon off={!media.audioEnabled} />
            </button>
            <button
              className="control"
              aria-pressed={media.videoEnabled}
              onClick={() => media.setVideoEnabled(!media.videoEnabled)}
              title={media.videoEnabled ? 'Turn camera off' : 'Turn camera on'}
            >
              <CamIcon off={!media.videoEnabled} />
            </button>
            <span className="faint">
              {media.starting
                ? 'Starting camera…'
                : media.stream
                  ? `${media.videoEnabled ? 'Camera on' : 'Camera off'} · ${media.audioEnabled ? 'Mic on' : 'Mic off'}`
                  : 'No camera'}
            </span>
            {/* The one thing the preview cannot show you. A camera proves itself
                the moment you see your own face; a microphone is silent until
                someone else tells you it was never working. */}
            {media.stream && media.audioEnabled ? <MicMeter segments={level} /> : null}
          </div>
        </div>

        <div className="cell cell-raised stack">
          <div>
            <label htmlFor="displayname">Your name</label>
            <input
              id="displayname"
              type="text"
              value={name}
              maxLength={48}
              placeholder="Guest"
              onChange={(e) => onNameChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !joining) onJoin();
              }}
            />
          </div>

          {mics.length > 1 ? (
            <div>
              <label htmlFor="mic">Microphone</label>
              <select
                id="mic"
                value={media.choice.audioInput ?? ''}
                onChange={(e) => media.selectDevice('audioInput', e.target.value)}
              >
                <option value="">Default</option>
                {mics.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Microphone ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {cameras.length > 1 ? (
            <div>
              <label htmlFor="cam">Camera</label>
              <select
                id="cam"
                value={media.choice.videoInput ?? ''}
                onChange={(e) => media.selectDevice('videoInput', e.target.value)}
              >
                <option value="">Default</option>
                {cameras.map((d, i) => (
                  <option key={d.deviceId} value={d.deviceId}>
                    {d.label || `Camera ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          {canChooseSpeaker() && speakers.length > 0 ? (
            <div>
              <label htmlFor="speaker">Speaker</label>
              <div className="row" style={{ gap: '0.5rem', flexWrap: 'nowrap' }}>
                <select
                  id="speaker"
                  value={media.choice.audioOutput ?? ''}
                  onChange={(e) => media.selectDevice('audioOutput', e.target.value)}
                  style={{ flex: 1, minWidth: 0 }}
                >
                  <option value="">Default</option>
                  {speakers.map((d, i) => (
                    <option key={d.deviceId} value={d.deviceId}>
                      {d.label || `Speaker ${i + 1}`}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => {
                    setTested(true);
                    void playTestTone(media.choice.audioOutput).finally(() =>
                      setTimeout(() => setTested(false), 1200),
                    );
                  }}
                  title="Play a short tone through the selected speaker"
                >
                  {tested ? 'Played' : 'Test'}
                </button>
              </div>
            </div>
          ) : null}

          {media.stream ? <BackgroundPicker background={background} /> : null}

          {media.error ? (
            <div className="notice bad" role="alert">
              {media.error}
              <div style={{ marginTop: '0.5rem' }}>
                <button className="ghost" onClick={media.retry}>
                  Retry
                </button>
              </div>
            </div>
          ) : null}

          <button className="primary" onClick={onJoin} disabled={joining}>
            {joining ? 'Joining…' : 'Join meeting'}
          </button>

          {media.error ? (
            <p className="faint">You can still join to listen and talk if a device becomes available.</p>
          ) : null}
        </div>
      </div>
    </main>
  );
}
